import { useCallback, useEffect, useState } from 'react'
import { Check, RefreshCcw, X } from 'lucide-react'
import Panel, { panelCard, panelGhostButton, panelIconButton } from '@/components/layout/Panel'
import { useLanguage } from '@/contexts/LanguageContext'

interface WdlPerson {
  discord_id: string
  display_name: string
  username: string
}

interface WdlApplication extends WdlPerson {
  id: string
  created_at: string
  in_clan: boolean
}

interface WdlStatus {
  configured: boolean
  auto_accept: boolean
  fetched_at: number
  error: '' | 'unauthorized' | 'unavailable'
  roster_size: number | null
  clan_size: number
  missing: { id: string; display_name: string; avatar_url: string; team: 'TLL' | 'TLLA' }[]
  outsiders: (WdlPerson & { role: string })[]
  applications: WdlApplication[]
  accepted: (WdlPerson & { accepted_at: number })[]
}

const subhead = 'mb-2 font-display text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground'
const chip = `${panelCard} flex items-center gap-1.5 px-2 py-1 text-xs`

/**
 * Wardogs League kadrosu ile klanın farkı — yalnızca yönetim (operator+).
 *
 * Otomatik kabul backend'de her 5 dakikada bir çalışır (backend/wdl/sync.py);
 * bu bant onun sonucunu gösterir ve klan dışından gelen başvurular için elle
 * kabul/red verir. Klandakilerin başvurusu burada hiç beklemez, o yüzden
 * listede kalan başvuru neredeyse her zaman klan dışındandır.
 */
export default function WdlRosterPanel({ onChanged }: { onChanged?: () => void }) {
  const { T } = useLanguage()
  const W = T.members.wdl
  const [st, setSt] = useState<WdlStatus | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (method: 'GET' | 'POST' = 'GET', url = '/api/wdl', body?: unknown) => {
    setBusy(true)
    setError('')
    try {
      const res = await fetch(url, {
        method,
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      })
      if (!res.ok) throw new Error(String(res.status))
      setSt(await res.json())
      if (method === 'POST') onChanged?.()
    } catch {
      setError(url === '/api/wdl' ? W.loadError : W.respondError)
    } finally {
      setBusy(false)
    }
  }, [W, onChanged])

  useEffect(() => { void load() }, [load])

  const respond = (app: WdlApplication, accept: boolean) => {
    if (!accept && !confirm(W.declineConfirm)) return
    void load('POST', `/api/wdl/applications/${encodeURIComponent(app.id)}/respond`, { accept })
  }

  const ago = (ts: number) => {
    const diff = Math.floor(Date.now() / 1000 - ts)
    if (diff < 60) return T.members.justNow
    if (diff < 3600) return T.members.mAgo.replace('{n}', String(Math.floor(diff / 60)))
    return T.members.hAgo.replace('{n}', String(Math.floor(diff / 3600)))
  }

  const action = st?.configured ? (
    <button className={panelGhostButton} disabled={busy} onClick={() => void load('POST', '/api/wdl/refresh')}>
      <RefreshCcw className={`h-3 w-3 ${busy ? 'animate-spin' : ''}`} />
      {W.refresh}
    </button>
  ) : undefined

  return (
    <Panel title={W.title} hint={W.hint} action={action}>
      {!st ? (
        <p className="text-xs text-muted-foreground">{error || T.members.loading}</p>
      ) : !st.configured ? (
        <p className="text-xs text-muted-foreground">{W.notConfigured}</p>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs">
            {st.roster_size !== null && (
              <span className="tabular-nums text-foreground/80">
                {W.stats
                  .replace('{roster}', String(st.roster_size))
                  .replace('{clan}', String(st.clan_size))
                  .replace('{missing}', String(st.missing.length))}
              </span>
            )}
            {st.fetched_at > 0 && (
              <span className="text-muted-foreground/60">{W.synced.replace('{t}', ago(st.fetched_at))}</span>
            )}
            {!st.auto_accept && <span className="text-amber-400">{W.autoAcceptOff}</span>}
          </div>

          {(st.error || error) && (
            <p className="rounded-sm border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              {error || (st.error === 'unauthorized' ? W.unauthorized : W.unavailable)}
            </p>
          )}

          {st.applications.length > 0 && (
            <div>
              <h3 className={subhead}>{W.pendingTitle.replace('{n}', String(st.applications.length))}</h3>
              <ul className="space-y-1.5">
                {st.applications.map(app => (
                  <li key={app.id} className={`${panelCard} flex items-center gap-2 px-3 py-2 text-sm`}>
                    <span className="truncate font-medium">{app.display_name || app.username}</span>
                    {app.username && <span className="truncate text-xs text-muted-foreground">@{app.username}</span>}
                    {!app.in_clan && (
                      <span className="shrink-0 text-[10px] uppercase tracking-wider text-amber-400">{W.pendingNotClan}</span>
                    )}
                    <div className="ml-auto flex gap-1.5">
                      <button className={panelIconButton} disabled={busy} title={W.accept} aria-label={W.accept}
                        onClick={() => respond(app, true)}>
                        <Check className="h-3.5 w-3.5" />
                      </button>
                      <button className={panelIconButton} disabled={busy} title={W.decline} aria-label={W.decline}
                        onClick={() => respond(app, false)}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {st.roster_size !== null && (
            <div>
              <h3 className={subhead}>{W.missingTitle.replace('{n}', String(st.missing.length))}</h3>
              {st.missing.length === 0 ? (
                <p className="text-xs text-muted-foreground/70">{W.missingNone}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {st.missing.map(m => (
                    <span key={m.id} className={chip}>
                      <img src={m.avatar_url} alt="" className="h-4 w-4 rounded-full" />
                      <span className="max-w-[10rem] truncate">{m.display_name}</span>
                      {m.team === 'TLLA' && <span className="text-[9px] font-bold text-sky-300">TLLA</span>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {st.outsiders.length > 0 && (
            <div>
              <h3 className={subhead}>{W.outsidersTitle.replace('{n}', String(st.outsiders.length))}</h3>
              <div className="flex flex-wrap gap-1.5">
                {st.outsiders.map(m => (
                  <span key={m.discord_id} className={chip}>
                    <span className="max-w-[10rem] truncate">{m.display_name || m.username}</span>
                    {m.username && <span className="text-muted-foreground/60">@{m.username}</span>}
                  </span>
                ))}
              </div>
            </div>
          )}

          {st.accepted.length > 0 && (
            <div>
              <h3 className={subhead}>{W.acceptedTitle}</h3>
              <div className="flex flex-wrap gap-1.5">
                {st.accepted.map(m => (
                  <span key={`${m.discord_id}-${m.accepted_at}`} className={chip}>
                    <Check className="h-3 w-3 text-emerald-400" />
                    <span className="max-w-[10rem] truncate">{m.display_name || m.username}</span>
                    <span className="text-muted-foreground/60">{ago(m.accepted_at)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Panel>
  )
}
